import { type Tel, nullTel } from '@w/tel'
import { safe } from '@w/utils'
import { generateDDL } from './core/ddl'
import {
  createSqlTag,
  isTransactionConflict,
  transactionSpan,
  type Query,
} from './core/sql'
import type { AnyColumn, Table, UnboundTable, WithFnBrand } from './core/table'
import type { View, UnboundView } from './core/view'
import type {
  DatabaseDriver,
  DbExtension,
  Dialect,
  QueryContext,
} from './core/types'

/**
 * Waits before attempts 2..8: SQLite's default busy handler schedule (`sqliteDefaultBusyCallback`, main.c),
 * 78 ms in total. Retrying at once lost to any holder that keeps the lock across a few event-loop turns
 * (an exclusive writer, a checkpoint, a replica apply), which under CPU load is all of them.
 */
const transactionRetryDelaysMs = [1, 2, 5, 10, 15, 20, 25] as const

/** Shared A2 runner. Callback may run up to 8 times; no effects outside the database. */
export async function runTransactionAttempts<R>(run: () => Promise<R>, enabled: boolean): Promise<R> {
  for (let attempt = 1; ; attempt++) {
    try {
      const result = await run()
      return result
    } catch (error) {
      if (!enabled || !isTransactionConflict(error)) throw error
      if (attempt === 8) {
        const code = typeof error === 'object' && error !== null && 'code' in error ? error.code : undefined
        throw Object.assign(new Error('Transaction failed after 8 attempts', { cause: error }), { code })
      }
      await new Promise(resolve => { setTimeout(resolve, transactionRetryDelaysMs[attempt - 1]) })
    }
  }
}

type AnyTable = Table<Record<string, AnyColumn>>
type AnyView = View<Record<string, AnyColumn>>
type AnyTableOrView = AnyTable | AnyView

/**
 * Type helper to convert unbound tables/views to bound tables/views
 */
type AnyUnbound =
  | UnboundTable<Record<string, AnyColumn>>
  | UnboundView<Record<string, AnyColumn>>

type BoundTables<T extends Record<string, AnyUnbound>> = {
  [K in keyof T]: T[K] extends UnboundView<infer Columns>
    ? View<Columns> & Columns
    : T[K] extends UnboundTable<infer Columns> & WithFnBrand<infer Fns>
      ? Table<Columns> & Columns & { fn: Fns }
      : never
}

/**
 * A lazy, awaitable handle for a database transaction.
 *
 * The transaction does **not** run on construction. It runs once, on the first
 * pull, when awaited (`then`/`catch`/`finally`). Pulling twice throws.
 * `await handle` resolves to the transaction result `R`.
 */
export class TransactionHandle<R> implements Promise<R> {
  private _state: 'pending' | 'started' = 'pending'

  // Structural `Promise` compatibility: lets a lazy handle be returned where a
  // `Promise<R>` is annotated (e.g. `return db.transaction(fn)`), so the lazy
  // change does not force call-site churn.
  readonly [Symbol.toStringTag] = 'Promise'

  constructor(private readonly _run: () => Promise<R>) {}

  get started(): boolean {
    return this._state === 'started'
  }

  private _start(): Promise<R> {
    if (this._state === 'started') {
      throw new Error(
        'Transaction already consumed. Each db.transaction(fn) handle runs once.',
      )
    }
    this._state = 'started'
    return this._run()
  }

  // oxlint-disable-next-line unicorn/no-thenable -- intentional thenable: the lazy tx runs on first pull when awaited
  then<TResult1 = R, TResult2 = never>(
    onfulfilled?: ((value: R) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: unknown) => TResult2 | PromiseLike<TResult2>) | null,
  ): Promise<TResult1 | TResult2> {
    return this._start().then(onfulfilled, onrejected)
  }

  catch<TResult = never>(
    onrejected?: ((reason: unknown) => TResult | PromiseLike<TResult>) | null,
  ): Promise<R | TResult> {
    return this._start().catch(onrejected)
  }

  finally(onfinally?: (() => void) | null): Promise<R> {
    return this._start().finally(onfinally)
  }

}

/**
 * Represents a database with typed tables.
 *
 * Provides:
 * - Type-safe table access (db.tableName)
 * - Dialect-aware query building via db.query
 * - Driver management for query execution
 * - Table-scoped fn methods (db.table.fn.method())
 * - DB-level fn methods (db.fn.method())
 */
export class Database<Tables extends Record<string, AnyTableOrView>> {
  private _driver: DatabaseDriver | null = null
  private _inTransaction = false
  private _transactionRetryManaged = false
  private readonly _tables: Tables
  private readonly _extensions: DbExtension[]
  // Mutable: setDriver adopts the driver's dialect (see setDriver).
  private _dialect: Dialect
  private readonly _tel: Tel
  private _boundQuery:
    | ((strings: TemplateStringsArray, ...values: unknown[]) => Query)
    | null = null

  /** Builder for db-level fn methods */
  // oxlint-disable-next-line typescript/no-explicit-any
  _dbFnBuilder?: (db: any) => any;

  // Type-safe table access is added dynamically
  [key: string]: unknown

  constructor(
    tables: Tables,
    dialect?: Dialect,
    tel?: Tel,
    extensions: DbExtension[] = [],
  ) {
    this._tables = tables
    this._extensions = extensions
    this._tel = tel ?? nullTel

    // Infer dialect from tables if not explicitly provided
    const tableList = Object.values(tables)
    if (tableList.length === 0) {
      this._dialect = dialect ?? 'postgres'
    } else {
      const inferredDialect = tableList[0].__dialect
      // Verify all tables use the same dialect
      for (const table of tableList) {
        if (table.__dialect !== inferredDialect) {
          throw new Error(
            `All tables must use the same dialect. Found "${inferredDialect}" and "${table.__dialect}".`,
          )
        }
      }
      this._dialect = dialect ?? inferredDialect
    }
    this._buildFns()
  }

  /**
   * Creates a QueryContext — a lightweight object with a tel-aware `query` and `withTel`.
   * The default context lazily reads from the db so setDriver() can be called after createDb().
   */
  _makeQueryContext(): QueryContext {
    const makeCtx = (
      getQuery: () => (
        strings: TemplateStringsArray,
        ...values: unknown[]
      ) => Query,
    ): QueryContext => ({
      get query() {
        return getQuery()
      },
      withTel: (tel: Tel): QueryContext =>
        makeCtx(() =>
          createSqlTag(
            this._dialect,
            safe(this._driver, 'Driver not set'),
            tel,
            this._transactionRetryManaged,
          ),
        ),
    })

    // Default: lazily read from db.query (respects later setDriver calls)
    return makeCtx(() => this.query)
  }

  /**
   * Builds per-db table wrappers so helpers can access db context, then attaches fn objects.
   * Uses Object.create() so column/table behavior falls through via prototype chain.
   */
  _buildFns(): void {
    const ctx = this._makeQueryContext()
    for (const [name, table] of Object.entries(this._tables)) {
      // Views do not need a db-bound wrapper.
      if ('_isView' in table && table._isView) {
        ;(this as Record<string, unknown>)[name] = table
        continue
      }

      const wrapper = Object.create(table) as Record<string, unknown>
      wrapper._queryContext = ctx

      if ('_fnBuilder' in table && typeof table._fnBuilder === 'function') {
        // oxlint-disable-next-line typescript-eslint/no-unsafe-assignment typescript-eslint/no-unsafe-call
        wrapper.fn = table._fnBuilder(wrapper, ctx)
      }

      ;(this as Record<string, unknown>)[name] = wrapper
    }
    if (this._dbFnBuilder) {
      ;(this as Record<string, unknown>).fn = this._dbFnBuilder(this)
    }
  }

  /**
   * Returns a new database instance with the given tel bound.
   * Child spans will be created under this tel for all queries.
   * All fn closures are rebuilt with the new tel context.
   */
  withTel(tel: Tel): this {
    const db = new Database(this._tables, this._dialect, tel, this._extensions)
    db._inTransaction = this._inTransaction
    db._transactionRetryManaged = this._transactionRetryManaged
    if (this._driver) {
      db.setDriver(this._driver)
    }
    // Copy fn builders and rebuild
    db._dbFnBuilder = this._dbFnBuilder
    if (db._dbFnBuilder) {
      db._buildFns()
    }
    return db as this
  }

  /**
   * Sets the database driver for query execution.
   * Must be called before using db.query.
   *
   * If the driver declares a `dialect`, the db adopts it — even when it
   * differs from the dialect the tables were authored in. This is what lets a
   * schema defined once with `sqliteTable` run against a postgres driver:
   * placeholders, fragment rendering, and `generateDDL` all follow the
   * driver's dialect, and DDL type mapping translates the authored column
   * types (see `generateTableDDL`).
   */
  setDriver(driver: DatabaseDriver): void {
    if (driver.dialect) this._dialect = driver.dialect
    this._driver = driver
    this._boundQuery = createSqlTag(
      this._dialect,
      driver,
      this._tel,
      this._transactionRetryManaged,
    )
  }

  /**
   * Executes raw SQL against the currently bound driver.
   *
   * Inside `db.transaction()`, this uses the transaction-bound driver.
   */
  exec(sql: string, params?: unknown[]): Promise<void> {
    if (!this._driver) {
      throw new Error(
        'No database driver set. Call db.setDriver(driver) before using db.exec.',
      )
    }
    return this._driver.exec(sql, params, { tel: this._tel })
  }

  /**
   * Gets the current database driver
   */
  get driver(): DatabaseDriver | null {
    return this._driver
  }

  /** Whether this handle belongs to a caller-owned transaction. */
  get inTransaction(): boolean {
    return this._inTransaction
  }

  /**
   * Gets all table definitions
   */
  get tables(): Tables {
    return this._tables
  }

  /**
   * Gets all database-level DDL extensions.
   */
  get extensions(): readonly DbExtension[] {
    return this._extensions
  }

  /**
   * Gets the dialect for this database
   */
  get dialect(): Dialect {
    return this._dialect
  }

  /**
   * Returns a lazy {@link TransactionHandle} that runs `fn` inside a database
   * transaction on first pull.
   *
   * The handle is a thenable: `await db.transaction(fn)` runs the tx and resolves
   * to its result `R`.
   *
   * The callback receives a shallow clone of this db with the transaction driver bound,
   * so queries inside `fn` run within the transaction. On throw the transaction rolls back.
   *
   * Callback may run up to 8 times; no effects outside the database.
   * Replica roots retry SQLITE_BUSY_SNAPSHOT and SQLITE_BUSY; nested transactions never retry.
   * Use exclusive for DDL. Long-open app transactions delay replica freeze.
   *
   * Chain with `withTel` for telemetry: `db.withTel(tel).transaction(fn)`.
   */
  transaction<R>(fn: (tx: this) => Promise<R>, options: { exclusive?: boolean } = {}): TransactionHandle<R> {
    if (!this._driver) {
      throw new Error(
        'No database driver set. Call db.setDriver(driver) before using db.transaction.',
      )
    }
    const driver = this._driver
    const retry = !this._inTransaction && 'concurrentTransactions' in driver && driver.concurrentTransactions === true
    const managed = retry || this._transactionRetryManaged
    return new TransactionHandle<R>(() =>
      transactionSpan(this._tel, this._transactionRetryManaged,
        { name: 'db.transaction', target: 'db', input: {} },
        spanTel => {
          const run = (attemptTel: Tel) => {
            const context = { tel: attemptTel, ...options, transactionRetryManaged: true }
            return driver.transaction(
              txDriver => {
                const txDb = this.withTel(attemptTel)
                txDb._inTransaction = true
                txDb._transactionRetryManaged = managed
                txDb.setDriver(txDriver)
                return fn(txDb)
              },
              context,
            )
          }
          return runTransactionAttempts(() => retry
            ? transactionSpan(spanTel, managed,
              { name: 'db.transaction.attempt', target: 'db', input: {} }, run)
            : run(spanTel), retry)
        },
      ),
    )
  }

  /**
   * Tagged template literal for building dialect-aware, executable SQL queries.
   *
   * This is the primary way to build and execute queries:
   * - Uses correct parameter placeholders for the dialect ($1 for postgres, ? for sqlite)
   * - Automatically binds the database driver for execution
   * - Supports nesting `sql` fragments for composition
   *
   * @example
   * ```ts
   * const db = createDb({ users });
   * db.setDriver(driver);
   *
   * // Simple query
   * const results = await db.query`
   *   SELECT ${db.users.all} FROM ${db.users} WHERE ${db.users.id.eq(id)}
   * `.execute(db.users.__selectSchema);
   *
   * // With conditional fragments
   * const results = await db.query`
   *   SELECT ${db.users.all} FROM ${db.users}
   *   WHERE TRUE
   *     ${filters.role && sql`AND ${db.users.role.eq(filters.role)}`}
   * `.execute(db.users.__selectSchema);
   * ```
   */
  get query(): (strings: TemplateStringsArray, ...values: unknown[]) => Query {
    if (!this._boundQuery) {
      throw new Error(
        'No database driver set. Call db.setDriver(driver) before using db.query.',
      )
    }
    return this._boundQuery
  }
}

/**
 * Creates a database definition with typed tables.
 *
 * Table names are derived from the object keys passed to createDb().
 * The dialect is automatically inferred from the tables passed in.
 * All tables must use the same dialect.
 *
 * @example
 * ```ts
 * // PostgreSQL
 * const users = pgTable({
 *   columns: (c) => ({ id: c.uuid().primaryKey() }),
 * });
 * const db = createDb({ users });  // table name: "users"
 * db.setDriver(driver);
 * await db.query`SELECT ${db.users.all} FROM ${db.users}`.execute(db.users.__selectSchema);
 *
 * // With fn methods
 * const users = pgTable({
 *   columns: (c) => ({ id: c.uuid().primaryKey(), name: c.text().notNull() }),
 *   fn: (table, db) => ({
 *     async getById(id: string) {
 *       return db.query`SELECT ${table.all} FROM ${table} WHERE ${table.id.eq(id)}`
 *         .executeOne(table.__selectSchema)
 *     },
 *   }),
 * });
 * const db = createDb({ users });
 * db.setDriver(driver);
 * const user = await db.users.fn.getById('abc');
 * ```
 */
/**
 * Full return type of createDb, including db-level fn and a withTel that preserves fn types.
 */
type CreateDbResult<
  Tables extends Record<string, AnyUnbound>,
  // oxlint-disable-next-line typescript/no-explicit-any
  DbFns = any,
> = Database<BoundTables<Tables>> &
  BoundTables<Tables> & {
    fn: DbFns
    withTel(tel: Tel): CreateDbResult<Tables, DbFns>
    transaction<R>(
      fn: (tx: CreateDbResult<Tables, DbFns>) => Promise<R>,
    ): TransactionHandle<R>
  }

type CreateDbFnBuilderDb<Tables extends Record<string, AnyUnbound>> = Database<
  BoundTables<Tables>
> &
  BoundTables<Tables> & {
    // oxlint-disable-next-line typescript/no-explicit-any -- fn is incomplete while fn builder is being constructed
    fn: any
    transaction<R>(
      // oxlint-disable-next-line typescript/no-explicit-any -- fn builders may pass tx to app helpers with narrower db aliases
      fn: (tx: any) => Promise<R>,
    ): TransactionHandle<R>
  }

export function createDb<Tables extends Record<string, AnyUnbound>, DbFns>(
  tables: Tables,
  options: {
    driver?: DatabaseDriver
    extensions?: DbExtension[]
    fn: (db: CreateDbFnBuilderDb<Tables>) => DbFns
  },
): CreateDbResult<Tables, DbFns>
export function createDb<Tables extends Record<string, AnyUnbound>>(
  tables: Tables,
  options?: {
    driver?: DatabaseDriver
    extensions?: DbExtension[]
  },
): CreateDbResult<Tables>
export function createDb<
  Tables extends Record<string, AnyUnbound>,
  // oxlint-disable-next-line typescript/no-explicit-any
  DbFns = any,
>(
  tables: Tables,
  options?: {
    driver?: DatabaseDriver
    extensions?: DbExtension[]
    fn?: (db: CreateDbFnBuilderDb<Tables>) => DbFns
  },
): CreateDbResult<Tables, DbFns> {
  // Phase 1: Set names on all unbound entries, then bind tables
  const boundEntries: Record<string, AnyTableOrView> = {}

  // Set _name on all entries so toString() works in view query closures
  for (const [name, unbound] of Object.entries(tables)) {
    unbound._name = name
  }

  // Bind tables first
  for (const [name, unbound] of Object.entries(tables)) {
    if (!('_isView' in unbound && unbound._isView)) {
      boundEntries[name] = unbound._bind(name)
    }
  }

  // Bind views second — query closures can now reference table/view names via toString()
  for (const [name, unbound] of Object.entries(tables)) {
    if ('_isView' in unbound && unbound._isView) {
      boundEntries[name] = (
        unbound as UnboundView<Record<string, AnyColumn>>
      )._bind(name)
    }
  }

  // Phase 2: Resolve lazy foreign key references (tables only)
  for (const entry of Object.values(boundEntries)) {
    if (!('_isView' in entry && entry._isView)) {
      for (const column of Object.values(entry.__columns)) {
        column._resolveLazyReferences()
      }
    }
  }

  const db = new Database(
    boundEntries as BoundTables<Tables>,
    undefined,
    undefined,
    options?.extensions,
  )
  if (options?.driver) {
    db.setDriver(options.driver)
  }

  // Store db-level fn builder and build all fns
  if (options?.fn) {
    // oxlint-disable-next-line typescript/no-explicit-any
    db._dbFnBuilder = options.fn as (db: any) => any
    db._buildFns()
  }

  return db as unknown as CreateDbResult<Tables, DbFns>
}

export async function testDb<
  Tables extends Record<string, AnyUnbound>,
  // oxlint-disable-next-line typescript/no-explicit-any
  DbFns = any,
>(
  tables: Tables,
  driver: DatabaseDriver,
  options?: {
    driver?: DatabaseDriver
    extensions?: DbExtension[]
    fn?: (db: CreateDbFnBuilderDb<Tables>) => DbFns
  },
): Promise<CreateDbResult<Tables, DbFns>> {
  const db = createDb(tables, options)
  db.setDriver(driver)
  await driver.exec(generateDDL(db))
  return db
}
